import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Dialog } from '@base-ui/react/dialog'
import { salaryPaymentSchema, type SalaryPaymentFormData } from '@/validators/employee'
import { useCreateSalaryPayment } from '@/hooks/useEmployees'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { MoneyInput } from '@/components/form/money-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { Employee } from '@/types/employee'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

const MONTH_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const PAYMENT_METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'BANK_TRANSFER', label: 'Transferência bancária' },
  { value: 'BANK_SLIP', label: 'Boleto' },
  { value: 'CASH', label: 'Dinheiro' },
  { value: 'OTHER', label: 'Outro' },
] as const

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

interface SalaryPaymentModalProps {
  employee: Employee
  open: boolean
  onClose: () => void
}

export function SalaryPaymentModal({ employee, open, onClose }: SalaryPaymentModalProps) {
  const createMutation = useCreateSalaryPayment(employee.id)
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const accounts = accountsData?.data ?? []

  const now = new Date()
  const defaultMonth = now.getMonth() + 1
  const defaultYear = now.getFullYear()
  const yearOptions = [defaultYear - 1, defaultYear, defaultYear + 1]

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SalaryPaymentFormData>({
    resolver: zodResolver(salaryPaymentSchema),
    defaultValues: {
      referenceMonth: defaultMonth,
      referenceYear: defaultYear,
      amount: Number.parseFloat(employee.baseSalary) as unknown as number,
      paymentDate: todayISO(),
      method: 'PIX',
      notes: '',
      accountId: '',
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        referenceMonth: defaultMonth,
        referenceYear: defaultYear,
        amount: Number.parseFloat(employee.baseSalary) as unknown as number,
        paymentDate: todayISO(),
        method: 'PIX',
        notes: '',
        accountId: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee.id])

  useEffect(() => {
    if (open && accounts.length > 0) {
      setValue('accountId', accounts.find((a) => a.isDefault)?.id ?? accounts[0]!.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountsData])

  async function onSubmit(data: SalaryPaymentFormData) {
    try {
      await createMutation.mutateAsync({
        ...data,
        paymentDate: new Date(data.paymentDate).toISOString(),
      })
      toast.success('Pagamento de salário registrado!')
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao registrar pagamento'))
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Registrar pagamento de salário
          </Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">{employee.name}</p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sp-month">Mês de referência *</Label>
                <Controller
                  name="referenceMonth"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="sp-month"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className={selectClass}
                    >
                      {MONTH_LABELS.map((label, i) => (
                        <option key={label} value={i + 1}>
                          {label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-year">Ano *</Label>
                <Controller
                  name="referenceYear"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="sp-year"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className={selectClass}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sp-amount">Valor pago *</Label>
                <MoneyInput id="sp-amount" error={!!errors.amount} {...register('amount')} />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
                <p className="text-xs text-muted-foreground">
                  Pré-preenchido com o salário base; ajuste se houver descontos ou horas extras.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-date">Data do pagamento *</Label>
                <Input
                  id="sp-date"
                  type="date"
                  aria-invalid={!!errors.paymentDate}
                  {...register('paymentDate')}
                />
                {errors.paymentDate && (
                  <p className="text-xs text-destructive">{errors.paymentDate.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-method">Forma de pagamento *</Label>
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <select id="sp-method" value={field.value} onChange={field.onChange} className={selectClass}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-account">Conta de pagamento *</Label>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <select id="sp-account" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.accountId && (
                <p className="text-xs text-destructive">{errors.accountId.message}</p>
              )}
              {accounts.length === 0 && (
                <p className="text-xs text-destructive">
                  Cadastre uma conta de caixa antes de registrar pagamentos.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-notes">Observações</Label>
              <Input id="sp-notes" {...register('notes')} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createMutation.isPending}
              >
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                loading={createMutation.isPending}
                loadingText="Registrando…"
              >
                Registrar
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
